import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { permission, scope } from '../../hoc/withPermissions'
import { usePermission } from '../../hooks/usePermission/usePermission'
import { useGamificationEnabled } from '../../hooks/useGamificationEnabled/useGamificationEnabled'
import { getAvailableProviders } from '../../hooks/useIntegrateWithCalendar/calendarProvider.utils'

import { IconRegistry } from '@percent/lemonade'
import { useAuth } from '@percent/workplace-giving/common/hooks/useAuth/useAuth'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { useLogout } from '@percent/workplace-giving/common/hooks/useLogout/useLogout'
import { getPartnerFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { config } from '@percent/workplace-giving/config/config'
import { Permissions } from '@percent/workplace-giving/constants/permissions'

export type NavIconName = 'home' | 'heart' | 'calendar-heart' | 'flash'

export type NavLinkConfig = {
  key: string
  label: string
  /** Shorter label for the bottom tab bar, where ~12 characters is the budget. */
  shortLabel: string
  to: string
  icon: NavIconName
  end?: boolean
}

export type NavMenuItem = {
  key: string
  label: string
  as: 'button'
  onClick: () => void
  icon?: keyof typeof IconRegistry
}

export type NavMenuSectionKey = 'profile' | 'help' | 'account'

export type NavMenuSection = {
  sectionKey: NavMenuSectionKey
  items: NavMenuItem[]
}

type MenuItemConfig = {
  key: string
  label: string
  condition: () => boolean
  action: () => void
  icon?: keyof typeof IconRegistry
}

// navLinks is ordered by priority: consumers that cannot show them all take
// from the front, so a destination is never duplicated across two navs.
export function useNavigationItems() {
  const { t } = useTranslation()
  const navigation = useNavigate()
  const authState = useAuth()
  const partner = getPartnerFromAuthState(authState.state)
  const has = usePermission()
  const { logout } = useLogout()
  const { track } = useAnalytics()

  const gamificationEnabled = useGamificationEnabled()
  const volunteeringEnabled = has(scope('volunteering'))
  const donationsEnabled = has(scope('donation_link'))
  const givingCampaignEnabled = has(scope('giving_campaign'))
  const canCreateCampaign = givingCampaignEnabled && has(permission(Permissions.CampaignsWrite))

  const navLinks: NavLinkConfig[] = [
    {
      key: 'home',
      label: t('workplace_giving.navigation.home'),
      shortLabel: t('workplace_giving.navigation.short.home'),
      to: AppRoute.HOME,
      icon: 'home',
      end: true
    },
    ...(donationsEnabled
      ? [
          {
            key: 'donate',
            label: t('workplace_giving.navigation.donate'),
            shortLabel: t('workplace_giving.navigation.short.donate'),
            to: AppRoute.DONATE,
            icon: 'heart' as const,
            end: true
          }
        ]
      : []),
    ...(volunteeringEnabled
      ? [
          {
            key: 'volunteer',
            label: t('workplace_giving.navigation.volunteer'),
            shortLabel: t('workplace_giving.navigation.short.volunteer'),
            to: AppRoute.VOLUNTEER,
            icon: 'calendar-heart' as const
          }
        ]
      : []),
    {
      key: 'myImpact',
      label: t('workplace_giving.navigation.myImpact'),
      shortLabel: t('workplace_giving.navigation.short.myImpact'),
      to: AppRoute.MY_IMPACT,
      icon: 'flash'
    }
  ]

  const accountMenuConfig: { sectionKey: NavMenuSectionKey; items: MenuItemConfig[] }[] = [
    {
      sectionKey: 'profile',
      items: [
        {
          key: 'myProfile',
          label: t('workplace_giving.navigation.myProfile'),
          condition: () => donationsEnabled || volunteeringEnabled,
          action: () => navigation(donationsEnabled ? AppRoute.MY_FUNDRAISER : AppRoute.MY_OPPORTUNITY)
        },
        {
          key: 'myFundraisers',
          label: t('workplace_giving.navigation.myFundraisers'),
          condition: () => has(scope('donation_link'), permission(Permissions.FundraiserWrite)),
          action: () => navigation(AppRoute.MY_FUNDRAISER)
        },
        {
          key: 'myOpportunities',
          label: t('workplace_giving.navigation.myOpportunities'),
          condition: () => volunteeringEnabled,
          action: () => navigation(AppRoute.MY_OPPORTUNITY)
        },
        {
          key: 'myCampaigns',
          label: t('workplace_giving.myCampaigns.title'),
          condition: () => canCreateCampaign,
          action: () => navigation(AppRoute.MY_CAMPAIGNS)
        },
        {
          key: 'myAchievements',
          label: t('workplace_giving.navigation.myAchievements'),
          condition: () => gamificationEnabled,
          action: () => navigation(AppRoute.MY_ACHIEVEMENTS)
        }
      ]
    },
    {
      sectionKey: 'help',
      items: [
        {
          key: 'helpCenter',
          label: t('workplace_giving.navigation.helpCenter'),
          condition: () => true,
          action: () => window.open(config.urls.helpCenterUrl, '_blank', 'noopener noreferrer'),
          icon: 'help-center'
        },
        {
          key: 'helpHub',
          label: partner?.helpHub?.title || t('workplace_giving.navigation.helpHub'),
          condition: () => !!partner?.helpHub?.link,
          action: () => {
            track({
              event: 'Partner Hub Link Clicked',
              properties: {
                helpHubLink: partner?.helpHub?.link
              }
            })
            window.open(partner?.helpHub?.link, '_blank', 'noopener noreferrer')
          },
          icon: 'help-hub'
        }
      ]
    },
    {
      sectionKey: 'account',
      items: [
        {
          key: 'settings',
          label: t('workplace_giving.navigation.settings'),
          condition: () =>
            getAvailableProviders(partner?.calendarIntegration).length > 0 ||
            partner?.features?.profileSecondaryEmails === 'enabled',
          action: () => navigation(AppRoute.SETTINGS)
        },
        {
          key: 'signout',
          label: t('workplace_giving.navigation.signOut'),
          condition: () => true,
          action: () => {
            logout()
          }
        }
      ]
    }
  ]

  // Lemonade's Menu drops empty sections and renders nothing when they are all
  // empty, so this no longer filters them out itself.
  const menuSections: NavMenuSection[] = accountMenuConfig.map(section => ({
    sectionKey: section.sectionKey,
    items: section.items
      .filter(item => item.condition())
      .map(item => ({
        key: item.key,
        label: item.label,
        as: 'button' as const,
        onClick: item.action,
        ...(item.icon && { icon: item.icon })
      }))
  }))

  return { navLinks, menuSections }
}
